/**
 * SSE (Server-Sent Events) 服务 - PC 管理端
 * 管理与后端的 SSE 长连接，用于接收实时通知推送
 *
 * 保活与重连策略：
 *  1. 服务端每 30s 主动推送 "heartbeat" 命名事件，前端通过 addEventListener 显式监听
 *     收到任何事件均会重置心跳超时计时器（45s 阈值）
 *  2. 心跳超时 / onerror 触发自动重连（指数退避，最多 5 次）
 *  3. 页面切回前台（visibilitychange）时主动校验，如已断开立即重连
 */
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import httpClient from './httpClient';

type EventCallback = (data: unknown) => void;

class SseService {
  private eventSource: EventSource | null = null;
  private isConnecting = false;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;
  private readonly reconnectDelay = 3000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly eventListeners = new Map<string, EventCallback[]>();

  /** 心跳超时计时器 - 服务端 30s 一次，给 45s 容忍 */
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly heartbeatTimeout = 45000;

  /** 页面可见性事件绑定标记，避免重复注册 */
  private visibilityHandlerBound = false;

  /**
   * 建立 SSE 连接
   */
  connect(): void {
    if (this.eventSource || this.isConnecting) {
      console.log('[SSE] Connection already exists or is connecting, skipping');
      return;
    }

    this.isConnecting = true;
    const sseUrl = API_ENDPOINTS.NOTIFICATION.SSE_SUBSCRIBE;

    try {
      this.eventSource = new EventSource(sseUrl, { withCredentials: true });

      this.eventSource.onopen = () => {
        console.log('[SSE] Connection established');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.resetHeartbeatTimer();
      };

      // 默认（未命名）消息事件
      this.eventSource.onmessage = (event: MessageEvent) => {
        console.log('[SSE] Default message received:', event.data);
        this.resetHeartbeatTimer();
        this.dispatchEvent('message', event.data);
      };

      // 服务端命名事件：heartbeat / connected / notification
      this.eventSource.addEventListener('heartbeat', (event: MessageEvent) => {
        // 收到服务端心跳，重置超时计时器（不打日志，避免刷屏）
        this.resetHeartbeatTimer();
      });

      this.eventSource.addEventListener('connected', (event: MessageEvent) => {
        console.log('[SSE] Connected event:', event.data);
        this.resetHeartbeatTimer();
        this.dispatchEvent('connected', event.data);
      });

      this.eventSource.addEventListener('notification', (event: MessageEvent) => {
        console.log('[SSE] Notification received:', event.data);
        this.resetHeartbeatTimer();
        this.dispatchEvent('notification', event.data);
      });

      this.eventSource.onerror = (error) => {
        console.error('[SSE] Connection error:', error);
        this.isConnecting = false;
        this.handleConnectionError();
      };

      this.bindVisibilityHandler();
    } catch (error) {
      console.error('[SSE] Failed to create EventSource:', error);
      this.isConnecting = false;
    }
  }

  /**
   * 断开 SSE 连接
   * @param notifyServer 是否通知后端清理连接（登出场景需要）
   */
  disconnect(notifyServer = false): void {
    this.clearHeartbeatTimer();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      console.log('[SSE] Connection closed');
    }

    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.eventListeners.clear();

    if (notifyServer) {
      // 异步通知后端，失败不阻塞前端登出流程
      httpClient
        .post(API_ENDPOINTS.NOTIFICATION.SSE_DISCONNECT)
        .catch((err) => {
          console.warn('[SSE] Notify server disconnect failed:', err);
        });
    }
  }

  /**
   * 注册事件监听器
   */
  on(eventType: string, callback: EventCallback): void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, []);
    }
    this.eventListeners.get(eventType)!.push(callback);
  }

  /**
   * 移除事件监听器
   */
  off(eventType: string, callback: EventCallback): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index !== -1) {
        listeners.splice(index, 1);
      }
    }
  }

  /**
   * 分发事件到已注册的监听器
   */
  private dispatchEvent(eventType: string, data: string): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      let parsedData: unknown = data;
      try {
        parsedData = JSON.parse(data);
      } catch {
        // data 非 JSON, 使用原始字符串
      }
      listeners.forEach((callback) => callback(parsedData));
    }
  }

  /**
   * 重置心跳超时计时器 - 每次收到服务端任何事件时调用
   */
  private resetHeartbeatTimer(): void {
    this.clearHeartbeatTimer();
    this.heartbeatTimer = setTimeout(() => {
      console.warn('[SSE] Heartbeat timeout, reconnecting...');
      this.handleHeartbeatTimeout();
    }, this.heartbeatTimeout);
  }

  private clearHeartbeatTimer(): void {
    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 处理心跳超时：关闭当前连接并重新建立
   */
  private handleHeartbeatTimeout(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.isConnecting = false;
    // 心跳触发的重连不计入失败次数
    this.reconnectAttempts = 0;
    this.connect();
  }

  /**
   * 处理连接错误，进行自动重连（指数退避）
   */
  private handleConnectionError(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.clearHeartbeatTimer();

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('[SSE] Max reconnect attempts reached, giving up');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;
    console.log(
      `[SSE] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
    );

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * 监听页面可见性变化 - 切回前台时若已断开则立即重连
   */
  private bindVisibilityHandler(): void {
    if (this.visibilityHandlerBound || typeof document === 'undefined') {
      return;
    }
    this.visibilityHandlerBound = true;
    document.addEventListener('visibilitychange', () => {
      if (
        document.visibilityState === 'visible' &&
        !this.eventSource &&
        !this.isConnecting
      ) {
        console.log('[SSE] Page visible, reconnecting...');
        this.reconnectAttempts = 0;
        this.connect();
      }
    });
  }
}

/** 全局单例 */
export const sseService = new SseService();

export default sseService;
